import { formatPesoAmount } from './money';
import { jsPDF, autoTable, loadImageAsDataUrl } from './pdfUtils';

const PLACEHOLDER_IMAGE = 'https://placehold.co/96';

const formatCurrency = (value) => `$${formatPesoAmount(Number(value || 0))}`;

const getItemImage = (item) =>
  item?.priceImage || item?.varietyImage || item?.productImage || PLACEHOLDER_IMAGE;

const getFormatDescription = (item) =>
  item?.priceDescription || item?.description || item?.metricUnit || null;

const getDataUrlFormat = (dataUrl) => (dataUrl?.includes('image/png') ? 'PNG' : 'JPEG');

export const getQuotationPdfAssetKey = (quotation) => {
  if (!quotation) return '';
  const itemImageKey = (quotation.items || [])
    .map((item) => getItemImage(item))
    .join('|');
  return `${quotation.id || 'new'}:${quotation.updatedAt || ''}:${itemImageKey}`;
};

export const preloadQuotationPdfImages = async (
  items = [],
  { concurrency = 4, onProgress } = {}
) => {
  const safeItems = Array.isArray(items) ? items : [];
  const total = safeItems.length;
  const assets = {};
  let nextIndex = 0;
  let completed = 0;

  const markProgress = () => {
    completed += 1;
    if (typeof onProgress === 'function') {
      onProgress(completed, total);
    }
  };

  if (total === 0) {
    if (typeof onProgress === 'function') onProgress(0, 0);
    return assets;
  }

  const workerCount = Math.max(1, Math.min(concurrency, total));
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < total) {
        const index = nextIndex;
        nextIndex += 1;
        try {
          const dataUrl = await loadImageAsDataUrl(getItemImage(safeItems[index]));
          if (dataUrl) {
            assets[index] = {
              dataUrl,
              format: getDataUrlFormat(dataUrl),
            };
          }
        } catch {
          // Individual image failures should not block the PDF.
        } finally {
          markProgress();
        }
      }
    })
  );

  return assets;
};

const resolveItemAmounts = (item) => {
  const qty = Number(item?.quantity) || 0;
  const explicitSubtotal = Number(item?.subtotal);
  const subtotal = Number.isFinite(explicitSubtotal) && explicitSubtotal >= 0
    ? Math.floor(explicitSubtotal)
    : Math.floor(((Number(item?.price) || 0) - (Number(item?.discount) || 0)) * qty);
  const unit = qty > 0
    ? subtotal / qty
    : (Number(item?.price) || 0) - (Number(item?.discount) || 0);
  const taxAmount = Number.isFinite(Number(item?.taxAmount)) ? Number(item.taxAmount) : null;
  const netAmount =
    Number.isFinite(Number(item?.netAmount))
      ? Number(item.netAmount)
      : taxAmount !== null
        ? subtotal - taxAmount
        : subtotal;
  const totalAmountLine =
    Number.isFinite(Number(item?.netAmount)) && taxAmount !== null
      ? netAmount + taxAmount
      : subtotal;
  return { qty, unit, netAmount, totalAmountLine };
};

const resolveTotals = (quotation) => {
  const items = Array.isArray(quotation?.items) ? quotation.items : [];
  const subtotal =
    typeof quotation?.itemsSubtotal === 'number'
      ? quotation.itemsSubtotal
      : items.reduce((sum, item) => {
          if (!item || item.quantity <= 0) return sum;
          const { totalAmountLine } = resolveItemAmounts(item);
          return sum + totalAmountLine;
        }, 0);
  const discount = Number(quotation?.orderDiscountAmount) || 0;
  const total = Number(quotation?.totalAmount) || Math.max(subtotal - discount, 0);
  const breakdown = Array.isArray(quotation?.taxBreakdown) ? quotation.taxBreakdown : [];
  const taxTotal = breakdown.reduce((sum, row) => sum + (Number(row.amount) || 0), 0);
  const net = Number.isFinite(Number(quotation?.netTotal))
    ? Number(quotation.netTotal)
    : Math.max(total - taxTotal, 0);
  return { subtotal, discount, total, net, breakdown };
};

const buildProductText = (item) => {
  const lines = [
    item?.productName || 'Producto',
    `Variedad: ${item?.varietyName || 'Variedad no especificada'}`,
  ];
  const formatDescription = getFormatDescription(item);
  if (formatDescription) lines.push(`Formato: ${formatDescription}`);
  if (item?.promotionSnapshot?.kind === 'temporary_price') lines.push(`Temporal: ${item.promotionName || 'Precio temporal'}`);
  if (item?.discountSource === 'promotion' && Number(item?.promotionDiscountAmount || 0) > 0) {
    lines.push(`Promo: ${item.promotionName || 'Promocion'}`);
  }
  return lines.join('\n');
};

const buildFileName = (quotation) => {
  const clientName = quotation?.Client?.name || 'cliente';
  const safeClientName = clientName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60) || 'cliente';
  return `cotizacion_${safeClientName}_${quotation?.id || 'detalle'}.pdf`;
};

export const resolveQuotationClientPricingCategoryLabels = (quotation) => {
  const labels = (quotation?.items || [])
    .filter((item) => String(item?.discountSource || '').trim().toLowerCase() === 'client_category')
    .map((item) => {
      const name = String(item?.clientPricingCategoryName || '').trim();
      if (name) return name;
      const id = Number(item?.clientPricingCategoryId);
      return Number.isInteger(id) ? `Categoría #${id}` : '';
    })
    .filter(Boolean);

  return Array.from(new Set(labels));
};

export const saveQuotationPdf = ({ quotation, excludedTaxLabels = [], imageAssets = {} }) => {
  const doc = new jsPDF('p', 'pt', 'a4');
  const marginX = 36;
  const pageWidth = doc.internal.pageSize.getWidth();
  let cursorY = 44;
  const clientPricingCategoryLabels = resolveQuotationClientPricingCategoryLabels(quotation);

  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const title = `Cotización #${quotation.id}`;
  doc.text(title, (pageWidth - doc.getTextWidth(title)) / 2, cursorY);
  cursorY += 24;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  if (quotation.updatedAt) {
    doc.text(`Actualizada: ${new Date(quotation.updatedAt).toLocaleString()}`, marginX, cursorY);
    cursorY += 14;
  }
  if (quotation.validUntil) {
    doc.text(`Válida hasta: ${new Date(quotation.validUntil).toLocaleDateString('es-CL')}`, marginX, cursorY);
    cursorY += 14;
  }

  if (quotation.Client) {
    cursorY += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Cliente', marginX, cursorY);
    cursorY += 13;
    doc.setFont('helvetica', 'normal');
    doc.text(quotation.Client.name || '', marginX, cursorY);
    cursorY += 12;
    if (quotation.Client.rut) {
      doc.text(`RUT: ${quotation.Client.rut}`, marginX, cursorY);
      cursorY += 12;
    }
    if (quotation.Client.email) {
      doc.text(`Email: ${quotation.Client.email}`, marginX, cursorY);
      cursorY += 12;
    }
    if (quotation.Client.address || quotation.Client.city) {
      const citySuffix = quotation.Client.city ? `, ${quotation.Client.city}` : '';
      doc.text(`Dirección: ${quotation.Client.address || ''}${citySuffix}`, marginX, cursorY);
      cursorY += 12;
    }
  }

  if (clientPricingCategoryLabels.length > 0) {
    cursorY += 4;
    const label = clientPricingCategoryLabels.length === 1
      ? 'Categoría de descuento'
      : 'Categorías de descuento';
    doc.setFont('helvetica', 'bold');
    doc.text(`${label}:`, marginX, cursorY);
    doc.setFont('helvetica', 'normal');
    doc.text(clientPricingCategoryLabels.join(', '), marginX + doc.getTextWidth(`${label}: `), cursorY);
    cursorY += 12;
  }

  cursorY += 10;

  const items = Array.isArray(quotation.items) ? quotation.items : [];
  const body = items.map((item) => {
    const { qty, unit, netAmount, totalAmountLine } = resolveItemAmounts(item);
    return [
      {
        content: buildProductText(item),
        styles: {
          cellPadding: { top: 7, right: 6, bottom: 7, left: 52 },
          minCellHeight: 52,
        },
      },
      item?.metricUnit || 'unidad',
      String(qty),
      formatCurrency(unit),
      formatCurrency(netAmount),
      formatCurrency(totalAmountLine),
    ];
  });

  if (body.length === 0) {
    body.push([
      { content: 'No hay productos en esta cotización.', colSpan: 6, styles: { halign: 'center' } },
    ]);
  }

  const totals = resolveTotals(quotation);
  const foot = [
    [
      { content: 'Valor neto', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
      { content: formatCurrency(totals.net), styles: { fontStyle: 'bold' } },
    ],
    ...totals.breakdown.map((row) => [
      {
        content: `${row.name || 'Impuesto'} ${row.rate ? `(${row.rate}%)` : ''}`.trim(),
        colSpan: 5,
        styles: { halign: 'right', fontStyle: 'bold' },
      },
      { content: formatCurrency(row.amount), styles: { fontStyle: 'bold' } },
    ]),
  ];

  if (Array.isArray(excludedTaxLabels) && excludedTaxLabels.length > 0) {
    foot.push([
      {
        content: `Impuestos excluidos: ${excludedTaxLabels.join(', ')}`,
        colSpan: 6,
        styles: { fontSize: 8, textColor: [75, 85, 99] },
      },
    ]);
  }

  if (totals.discount > 0) {
    foot.push([
      { content: 'Descuento', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', textColor: [220, 38, 38] } },
      { content: `-${formatCurrency(totals.discount)}`, styles: { fontStyle: 'bold', textColor: [220, 38, 38] } },
    ]);
  }

  foot.push([
    { content: 'Total', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold' } },
    { content: formatCurrency(totals.total), styles: { fontStyle: 'bold', textColor: [29, 78, 216] } },
  ]);

  autoTable(doc, {
    startY: cursorY,
    margin: { left: marginX, right: marginX },
    head: [['Producto', 'Unidad', 'Cantidad', 'Precio', 'Valor neto', 'Valor total']],
    body,
    foot,
    theme: 'grid',
    styles: {
      fontSize: 8.5,
      cellPadding: 5,
      overflow: 'linebreak',
      valign: 'middle',
      lineColor: [229, 231, 235],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [75, 85, 99],
      fontStyle: 'bold',
      fontSize: 8,
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [17, 24, 39],
      fontSize: 8.5,
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251],
    },
    columnStyles: {
      0: { cellWidth: 220 },
      1: { cellWidth: 54 },
      2: { cellWidth: 50, halign: 'right' },
      3: { cellWidth: 58, halign: 'right' },
      4: { cellWidth: 65, halign: 'right' },
      5: { cellWidth: 72, halign: 'right' },
    },
    didDrawCell: (data) => {
      if (data.cell.section !== 'body' || data.column.index !== 0) return;
      const asset = imageAssets[data.row.index];
      if (!asset?.dataUrl) return;
      const size = 38;
      const x = data.cell.x + 7;
      const y = data.cell.y + (data.cell.height - size) / 2;
      try {
        doc.addImage(asset.dataUrl, asset.format || 'JPEG', x, y, size, size);
      } catch (err) {
        console.warn('No se pudo agregar la imagen al PDF:', err);
      }
    },
  });

  doc.save(buildFileName(quotation));
};
